import { injectable, inject } from 'inversify';
import { Message, Field, Type } from "protobufjs/light";
import { Result, ok, err } from '@xlearn/common'
import * as SerializationApi from '@/app/state-serialization/api'
import { ProtobufSerializer } from '@/app/state-serialization/infrastructure';
import * as Api from '@/app/state-serialization/api'
import { QuizSessionPhase } from '../api'
import { QuizSessionTrackingState } from '../common'
import { reportNonFatalError } from '@/app/error-handling';

@injectable()
export class QuizSessionStateProtobufSerializer implements Api.StateSerializationPlugin<QuizSessionTrackingState[]> {

  key = { id: 6, name: { ns: 'suspend-data', name: 'quizSessions' }, format: 'protobuf' }

  constructor(
    @inject(SerializationApi.TypedStateSerializerSymbol)
    private serializer: SerializationApi.TypedStateSerializer) {
  }

  serialize(input: QuizSessionTrackingState[]): Result<Uint8Array, Api.SerializerError> {

    let sessionMessages = []
    for (let session of input) {
      const contentResult = this.serializeContent(session.type, session.content)
      if (!contentResult.success) {
        this.displaySerializationError(session.type, contentResult.error)
        continue
      }

      const sessionMessage = new QuizSessionStateProtoMessage({
        id: session.id,
        quizId: session.quizId,
        aspect: session.aspect,
        type: session.type,
        phase: mapPhase(session.phase),
        content: contentResult.value
      })
      sessionMessages.push(sessionMessage)
    }
    
    const message = new QuizSessionStatesProtoMessage({
      sessions: sessionMessages
    })
    return ProtobufSerializer.serialize<QuizSessionStatesProtoMessage>(
      message, QuizSessionStatesProtoMessage)

    function mapPhase(input: QuizSessionPhase): QuizSessionPhaseProtoEnum {
      switch (input) {
        case '':
          return QuizSessionPhaseProtoEnum.none
        case 'active':
          return QuizSessionPhaseProtoEnum.active
        case 'aborted':
          return QuizSessionPhaseProtoEnum.aborted
        case 'finished':
          return QuizSessionPhaseProtoEnum.finished
      }
    }
  }

  private serializeContent(
    type: string, state: unknown): Result<Uint8Array, SerializationApi.TypedStateSerializeError> {

    const serializeResult = this.serializer.serialize(this.serializerName(type), 'protobuf', state)
    if (serializeResult.success) {
      return ok(serializeResult.value.buffer)
    }

    return err(serializeResult.error)
  }

  deserialize(content: Uint8Array): Result<QuizSessionTrackingState[], Api.DeserializerError> {

    const containerResult = ProtobufSerializer.deserialize<QuizSessionStatesProtoMessage>(
      content,
      QuizSessionStatesProtoMessage)

    if (!containerResult.success) {
      return containerResult
    }

    let sessions = []
    for (let sessionMessage of containerResult.value.sessions) {
      const contentResult = this.deserializeContent(sessionMessage.type, sessionMessage.content)
      if (!contentResult.success) {
        this.displaySerializationError(sessionMessage.type, contentResult.error)
        continue
      }

      const session: QuizSessionTrackingState = {
        id: sessionMessage.id,
        quizId: sessionMessage.quizId,
        aspect: sessionMessage.aspect,
        type: sessionMessage.type,
        phase: mapPhase(sessionMessage.phase),
        content: contentResult.value
      }

      sessions.push(session)
    }

    return ok(sessions)

    function mapPhase(input: QuizSessionPhaseProtoEnum): QuizSessionPhase {
      switch (input) {
        case QuizSessionPhaseProtoEnum.none:
          return ''
        case QuizSessionPhaseProtoEnum.active:
          return 'active'
        case QuizSessionPhaseProtoEnum.aborted:
          return 'aborted'
        case QuizSessionPhaseProtoEnum.finished:
          return 'finished'
      }
    }
  }

  private deserializeContent(type: string,
                             buffer: Uint8Array): Result<unknown, SerializationApi.TypedStateDeserializeError> {

    const deserializeResult = this.serializer.deserialize(this.serializerName(type), 'protobuf', buffer)
    if (deserializeResult.success) {
      return ok(deserializeResult.value.content)
    }

    return err(deserializeResult.error)
  }

  private serializerName(type: string) {
    return SerializationApi.stateSerializerName('suspend-data', `quiz-session-${type}`)
  }

  private displaySerializationError(type: string, error: SerializationApi.TypedStateSerializeError | SerializationApi.TypedStateDeserializeError) {
    switch (error.code) {
      case 'serializer-not-found':
        const name = error.details
        reportNonFatalError(`quiz session 'protobuf' serializer not found with name '${JSON.stringify(name)}', ` +
              `session won't be included`)
        break
      case 'serialize-error':
        reportNonFatalError(`quiz session 'protobuf' serialization error for type '${type}', ` +
              `session won't be included in saved data`, error.details)
      case 'deserialize-error':
        reportNonFatalError(`quiz session 'protobuf' deserialization error for type '${type}', ` +
              `session won't be included in loaded data`, error.details)
        break
    }
  }
}

// type SerializationError = {
//   code: 'serializer-not-found', details: Api.StateSerializerName }
//       | { code: 'serialize-error', details: Api.SerializerError }
//       | { code: 'deserialize-error', details: Api.DeserializerError }

enum QuizSessionPhaseProtoEnum {
  none = 0,
  active = 1,
  aborted = 2,
  finished = 3
}

@Type.d('QuizSessionStateProtoMessage')
class QuizSessionStateProtoMessage extends Message<QuizSessionStateProtoMessage> {

  @Field.d(1, 'string', "optional", '')
  id: string

  @Field.d(2, 'string', "optional", '')
  quizId: string

  @Field.d(3, 'string', "optional", '')
  aspect: string

  @Field.d(4, 'string', "optional", '')
  type: string

  @Field.d(5, QuizSessionPhaseProtoEnum, "optional", QuizSessionPhaseProtoEnum.none)
  phase: QuizSessionPhaseProtoEnum

  @Field.d(6, 'bytes', 'optional', [])
  public content: Uint8Array
}

@Type.d('QuizSessionStatesProtoMessage')
class QuizSessionStatesProtoMessage extends Message<QuizSessionStatesProtoMessage> {

  @Field.d(1, 'uint32', 'optional', 1)
  public schemaVersion: number

  @Field.d(2, QuizSessionStateProtoMessage, 'repeated')
  public sessions: QuizSessionStateProtoMessage[]

  
}

